import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  DollarSign, 
  Shield, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  HelpCircle,
  TrendingUp,
  FileText,
  Scale,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Lightbulb,
  Target
} from 'lucide-react';

const ReserveEducation = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What is a merchant account reserve?",
      answer: "A merchant account reserve is a portion of your credit card sales that your payment processor holds back as security against potential chargebacks, refunds, or other financial risks. Think of it as a security deposit that protects the processor if your business faces financial difficulties or disputes."
    },
    {
      question: "Why do payment processors require reserves?",
      answer: "Payment processors face liability for chargebacks and refunds even after they've paid you for transactions. If a customer disputes a charge months later and you can't cover it, the processor is on the hook. Reserves protect them from this risk, especially for businesses in high-risk industries or those with limited processing history."
    },
    {
      question: "How long can a processor hold my reserves?",
      answer: "Reserve hold periods vary widely - from 90 days to 12 months or longer. The timeframe depends on your industry, chargeback history, processing volume, and the specific terms in your merchant agreement. Some processors release reserves gradually (rolling reserves), while others hold a fixed amount until account closure."
    },
    {
      question: "Can I negotiate my reserve terms?",
      answer: "Yes! Reserve terms are often negotiable, especially if you have a strong processing history, low chargeback rates, and stable financials. You can negotiate the percentage held, the release timeline, and sometimes eliminate reserves entirely after proving your business is low-risk."
    },
    {
      question: "What's the difference between rolling and fixed reserves?",
      answer: "A rolling reserve holds a percentage of each day's transactions for a set period (e.g., 10% for 180 days), then releases funds on a rolling basis. A fixed reserve (or capped reserve) holds a set dollar amount and stops once that cap is reached. Rolling reserves are more common for new merchants."
    },
    {
      question: "When should I be concerned about my reserves?",
      answer: "Be concerned if: your processor increases reserves without clear explanation, reserves aren't being released as scheduled, you can't get clear answers about release timelines, or your reserve amount exceeds what's stated in your contract. These could indicate problems that require professional help."
    },
    {
      question: "What happens to my reserve if I close my merchant account?",
      answer: "When you close your account, most processors hold your reserve for an additional period (often 6-12 months) to cover any chargebacks that may arise from past transactions. The chargeback window for most card networks is 120 days, but some disputes can be filed later."
    },
    {
      question: "Can a processor keep my reserve indefinitely?",
      answer: "No. While processors have legitimate reasons to hold reserves, they cannot keep your money indefinitely without cause. If your processor is refusing to release funds beyond the contractual timeframe without valid justification, you may have legal recourse."
    }
  ];

  const reserveTypes = [
    {
      title: "Rolling Reserve",
      icon: TrendingUp,
      description: "A percentage of daily sales held for a fixed period, then released on a rolling basis.",
      example: "Example: 10% of each day's sales held for 180 days. On day 181, the reserve from day 1 is released, and so on.",
      color: "purple"
    },
    {
      title: "Capped Reserve",
      icon: Target,
      description: "A fixed dollar amount is held until the cap is reached, then no additional funds are withheld.",
      example: "Example: Processor holds 10% until reserve reaches $50,000, then stops withholding.",
      color: "cyan"
    },
    {
      title: "Up-Front Reserve",
      icon: Shield,
      description: "A lump sum required before processing begins, often for high-risk merchants.",
      example: "Example: Merchant must deposit $25,000 before activating their account.",
      color: "amber"
    }
  ];

  const riskFactors = [
    { factor: "High chargeback rates", impact: "Higher reserve percentage" },
    { factor: "New business (< 2 years)", impact: "Longer hold periods" },
    { factor: "High-risk industry", impact: "Up-front reserves required" },
    { factor: "Large average transaction size", impact: "Increased reserve cap" },
    { factor: "Subscription/recurring billing", impact: "Extended release timeline" },
    { factor: "International sales", impact: "Additional reserve requirements" },
    { factor: "Previous processor issues", impact: "Stricter terms overall" }
  ];

  return (
    <div className="marketing-layout min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-lg bg-slate-900/50 border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <img src="/logo.png" alt="ReserveRelease.AI" className="h-10 sm:h-14 md:h-16 object-contain" />
            <div className="flex flex-col">
              <span className="text-lg sm:text-2xl md:text-3xl font-bold whitespace-nowrap">
                <span className="bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-500 bg-clip-text text-transparent">
                  Reserve<span className="hidden xs:inline">Release</span>
                </span>
                <span className="text-white">.AI</span>
              </span>
              <span className="text-xs sm:text-sm text-slate-400 hidden sm:block">Reserve Recovery Solutions</span>
            </div>
          </Link>
          <div className="flex gap-1 sm:gap-4 items-center flex-shrink-0">
            <Link to="/" className="flex items-center gap-1 text-white hover:text-purple-400 transition-colors text-xs sm:text-base px-2 sm:px-0">
              <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <Link to="/login" className="primary-button text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3">
              <span className="hidden sm:inline">Client Portal</span>
              <span className="sm:hidden">Login</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section pt-24 sm:pt-28 pb-16">
        <div className="hero-glow"></div>
        <div className="container mx-auto px-4 sm:px-6 md:px-12 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full mb-6">
              <BookOpen size={18} className="text-purple-400" />
              <span className="text-sm font-medium text-white">Educational Resource</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Understanding{' '}
              <span className="gradient-text">Merchant Account Reserves</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
              Everything you need to know about payment processor reserves, why they exist, 
              and how to protect your business from unfair holds.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Are Reserves Section */}
      <section className="section-padding bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 flex items-center justify-center">
                <DollarSign className="text-white" size={28} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">What Are Merchant Reserves?</h2>
            </motion.div>
            
            <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
              <p>
                When you accept credit card payments, the funds don't always stay in your account permanently. 
                Payment processors often hold back a portion of your sales as a <span className="text-purple-400 font-semibold">reserve</span> — 
                a financial safety net that protects them against potential losses.
              </p>
              <p>
                These reserves exist because of the unique nature of card payments. Unlike cash transactions, 
                credit card sales can be reversed through chargebacks months after the original purchase. 
                If your business can't cover these reversals, the processor bears the financial responsibility.
              </p>
              <p>
                While reserves serve a legitimate purpose, they can create significant cash flow challenges 
                for businesses — especially when processors hold funds longer than necessary or impose 
                excessive reserve requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Reserves */}
      <section className="section-padding bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Types of Reserve Accounts</h2>
              <p className="text-lg text-slate-400">Understanding the different ways processors structure reserves</p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {reserveTypes.map((type, index) => (
                <motion.div 
                  key={index} 
                  className="glass-card rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${
                    type.color === 'purple' ? 'from-purple-500 to-purple-600' :
                    type.color === 'cyan' ? 'from-cyan-500 to-cyan-600' :
                    'from-amber-500 to-amber-600'
                  } flex items-center justify-center mb-4`}>
                    <type.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{type.title}</h3>
                  <p className="text-slate-400 mb-4">{type.description}</p>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-300 border border-white/5">
                    <strong className="text-purple-400">Example:</strong> {type.example.replace('Example: ', '')}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Risk Factors */}
      <section className="section-padding bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                <AlertTriangle className="text-white" size={28} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">What Affects Your Reserve Requirements?</h2>
            </motion.div>
            
            <p className="text-lg text-slate-300 mb-8">
              Processors evaluate several factors when determining your reserve terms. Understanding these 
              can help you negotiate better conditions or reduce your requirements over time.
            </p>
            
            <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-6 py-4 font-semibold text-white">Risk Factor</th>
                    <th className="text-left px-6 py-4 font-semibold text-white">Typical Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {riskFactors.map((item, index) => (
                    <tr key={index} className="border-t border-white/5">
                      <td className="px-6 py-4 text-slate-300">{item.factor}</td>
                      <td className="px-6 py-4 text-slate-400">{item.impact}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Your Rights Section */}
      <section className="section-padding bg-slate-950">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                <Scale className="text-white" size={28} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Know Your Rights</h2>
            </motion.div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div 
                className="glass-card rounded-2xl p-6 border border-green-500/30"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="font-bold text-white mb-4 flex items-center gap-2 text-lg">
                  <CheckCircle className="text-green-400" size={24} />
                  You Have the Right To:
                </h3>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 mt-1">•</span>
                    <span>Clear documentation of reserve terms in your merchant agreement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 mt-1">•</span>
                    <span>Explanation of why reserves are required</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 mt-1">•</span>
                    <span>Specific release timelines and conditions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 mt-1">•</span>
                    <span>Regular statements showing reserve balances</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 mt-1">•</span>
                    <span>Written notice before reserve terms change</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 mt-1">•</span>
                    <span>Release of funds as specified in your contract</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div 
                className="glass-card rounded-2xl p-6 border border-red-500/30"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="font-bold text-white mb-4 flex items-center gap-2 text-lg">
                  <AlertTriangle className="text-red-400" size={24} />
                  Warning Signs of Problems:
                </h3>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">•</span>
                    <span>Reserves increased without written justification</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">•</span>
                    <span>Funds not released according to schedule</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">•</span>
                    <span>Vague or evasive responses about release dates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">•</span>
                    <span>Reserve percentages exceeding industry norms</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">•</span>
                    <span>Sudden account termination with reserve retention</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">•</span>
                    <span>Processor unresponsive to inquiries</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                <Lightbulb className="text-white" size={28} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Tips for Managing Reserves</h2>
            </motion.div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: "Read Your Agreement Carefully", desc: "Before signing, understand exactly what percentage will be held, for how long, and under what conditions funds will be released." },
                { title: "Keep Chargebacks Low", desc: "The best way to reduce or eliminate reserves is maintaining a clean chargeback record. Stay below 1% chargeback ratio." },
                { title: "Build Processing History", desc: "Processors often reduce reserves after 6-12 months of stable processing with low disputes. Ask for a review periodically." },
                { title: "Document Everything", desc: "Keep copies of all agreements, amendments, and correspondence. This documentation is crucial if disputes arise." },
                { title: "Negotiate Terms", desc: "Reserve terms are often negotiable. If you have strong financials or processing history, use it as leverage." },
                { title: "Seek Help When Needed", desc: "If your processor is holding funds unfairly, professional assistance can help recover what's rightfully yours." }
              ].map((tip, index) => (
                <motion.div 
                  key={index}
                  className="glass-card rounded-xl p-5 border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h4 className="font-semibold text-white mb-2">{index + 1}. {tip.title}</h4>
                  <p className="text-slate-400 text-sm">{tip.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <HelpCircle className="text-white" size={28} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Frequently Asked Questions</h2>
            </motion.div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div 
                  key={index}
                  className="glass-card rounded-xl border border-white/10 overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-semibold text-white pr-4">{faq.question}</span>
                    {expandedFaq === index ? (
                      <ChevronUp className="text-purple-400 flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-slate-400 flex-shrink-0" size={20} />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-4 text-slate-300 border-t border-white/5 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-purple-900/50 to-cyan-900/50">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Are Your Reserves Being{' '}
              <span className="gradient-text">Held Unfairly?</span>
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 mb-8">
              If your payment processor is holding your funds longer than agreed or refusing 
              to release your reserves, we can help. Our experts specialize in recovering 
              frozen merchant funds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <button className="primary-button text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 w-full sm:w-auto">
                  Start Your Recovery
                </button>
              </Link>
              <Link to="/login">
                <button className="secondary-button text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 w-full sm:w-auto">
                  Client Login
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-8 sm:py-12 border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center text-slate-400">
            <p className="mb-2">&copy; 2025 ReserveRelease.AI. All rights reserved.</p>
            <div className="flex justify-center gap-6 mb-4">
              <Link to="/" className="text-purple-400 hover:text-purple-300 transition-colors">Home</Link>
              <Link to="/reserve-education" className="text-purple-400 hover:text-purple-300 transition-colors">Education Center</Link>
              <Link to="/login" className="text-purple-400 hover:text-purple-300 transition-colors">Client Portal</Link>
            </div>
            <p className="text-sm mb-6">Specialized in merchant reserve recovery and payment processing litigation.</p>
            
            <div className="border-t border-white/10 pt-6 mt-6">
              <p className="text-xs text-slate-500 max-w-4xl mx-auto leading-relaxed">
                <strong className="text-slate-400">DISCLAIMER:</strong> The information on this website is for general information purposes only. Nothing on this site should be taken as legal advice for any individual case or situation. This information is not intended to create, and receipt of viewing does not constitute, an attorney client relationship. Prior results do not guarantee a similar outcome.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReserveEducation;
